import { CROSSWORD_HINT_WORD_LIMIT, GRID_HINT_WORD_LIMIT } from "../crosswordHintWordLimit";
import { normalizeWord } from "./PuzzlePlayLogic";

/**
 * First cell for first-letter grid hints — same rules as `GridGrid` (direction, reversed, scattered).
 * @param {object} coord - filledCoordinates entry
 * @returns {{ row: number, col: number } | null}
 */
export function gridCoordGetFirstPosition(coord) {
    const positions = coord?.positions || [];
    if (positions.length === 0) return null;
    if ((coord.direction || "").trim().toLowerCase() === "scattered") return null;

    const word = (coord.text || coord.word || "").trim();
    if (!word) return positions[0] || null;
    const firstChar = word[0].toLowerCase();
    const reversed = coord.reversed === true || coord.reversed === "true";

    const withFirstChar = positions.filter((p) => (p.value || "").toLowerCase() === firstChar);
    if (withFirstChar.length === 1) {
        return withFirstChar[0];
    }
    if (withFirstChar.length > 1) {
        if (reversed) {
            for (let i = positions.length - 1; i >= 0; i -= 1) {
                if ((positions[i].value || "").toLowerCase() === firstChar) {
                    return positions[i];
                }
            }
        } else {
            for (const p of positions) {
                if ((p.value || "").toLowerCase() === firstChar) {
                    return p;
                }
            }
        }
    }

    if (reversed) {
        return positions[positions.length - 1] || null;
    }
    if (positions.length > 1) {
        const lastVal = (positions[positions.length - 1].value || "").toLowerCase();
        const firstVal = (positions[0].value || "").toLowerCase();
        if (lastVal === firstChar && firstVal && firstVal !== firstChar) {
            return positions[positions.length - 1];
        }
    }
    return positions[0] || null;
}

/**
 * First N cells along a grid word path (handles reversed). Used for paid letter hints.
 * @param {object} coord
 * @param {number} [n=2]
 * @returns {{ row: number, col: number }[]}
 */
export function gridCoordGetFirstNPositions(coord, n = 2) {
    const count = Math.max(0, Math.floor(Number(n) || 0));
    if (count <= 0) return [];
    const positions = coord?.positions || [];
    if (positions.length === 0) return [];
    if ((coord.direction || "").trim().toLowerCase() === "scattered") return [];

    const reversed = coord.reversed === true || coord.reversed === "true";
    const first = gridCoordGetFirstPosition(coord);
    if (!first) return [];

    const firstIdx = positions.findIndex(
        (p) => Number(p.row) === Number(first.row) && Number(p.col) === Number(first.col),
    );
    if (firstIdx < 0) {
        return [{ row: Number(first.row), col: Number(first.col) }];
    }

    const ordered = [];
    if (reversed) {
        for (let i = firstIdx; i >= 0 && ordered.length < count; i -= 1) {
            ordered.push({ row: Number(positions[i].row), col: Number(positions[i].col) });
        }
    } else {
        for (let i = firstIdx; i < positions.length && ordered.length < count; i += 1) {
            ordered.push({ row: Number(positions[i].row), col: Number(positions[i].col) });
        }
    }
    return ordered;
}

function normalizeFilledCoords(filledCoordinates) {
    if (!filledCoordinates) return [];
    if (Array.isArray(filledCoordinates)) return filledCoordinates;
    if (typeof filledCoordinates === "string") {
        try {
            return JSON.parse(filledCoordinates);
        } catch {
            return [];
        }
    }
    return [];
}

/**
 * Word list encoded in API `hintUsed`: newline-separated string, optional JSON array, or literal "\\n" escapes.
 * @param {unknown} hintUsedRaw
 * @returns {string[]}
 */
export function parseHintUsedWordLines(hintUsedRaw) {
    if (hintUsedRaw == null) return [];
    if (Array.isArray(hintUsedRaw)) {
        return hintUsedRaw.map((x) => String(x ?? "").trim()).filter(Boolean);
    }
    let s = typeof hintUsedRaw === "string" ? hintUsedRaw : String(hintUsedRaw);
    s = s.replace(/\\n/g, "\n");
    return s.split(/\r\n|\r|\n/g).map((x) => x.trim()).filter(Boolean);
}

/** Canonical multiline string for storing `hintUsed` in client state. */
export function normalizeHintUsedToMultilineString(hintUsedRaw) {
    const lines = parseHintUsedWordLines(hintUsedRaw);
    return lines.join("\n");
}

/**
 * Words remaining that may receive a new hint letter this session (after server `hintUsed` newline list).
 * @param {string|null|undefined} hintUsedRaw - e.g. play/template `hintUsed` string
 * @param {number} configuredLimit - env cap (e.g. CROSSWORD_HINT_WORD_LIMIT)
 */
export function hintRevealSlotCount(hintUsedRaw, configuredLimit) {
    const cap =
        typeof configuredLimit === "number" && Number.isFinite(configuredLimit) && configuredLimit > 0
            ? configuredLimit
            : 3;
    const used = parseHintUsedWordLines(hintUsedRaw).length;
    return Math.max(0, cap - used);
}

/**
 * Effective `hintUsed` string for slot budget while hint-used PUT is in flight (avoids hint-set flicker).
 */
export function hintUsedStringWithPendingAppends(baseHintUsed, pendingWords) {
    if (!pendingWords?.length) {
        return normalizeHintUsedToMultilineString(baseHintUsed);
    }
    const b = normalizeHintUsedToMultilineString(baseHintUsed);
    const extra = pendingWords.map((w) => String(w ?? "").trim()).filter(Boolean).join("\n");
    if (!extra) return b;
    return b ? `${b}\n${extra}` : extra;
}

/**
 * Normalized words that currently receive a first-letter hint (before the just-solved word is added).
 * Matches `GridGrid` `isFirstCharacter` / `hintOrderedWords` logic.
 *
 * @param {object[]|string} filledCoordinates
 * @param {string[]} wordsFoundList - words already found this session (normalized)
 * @param {string[]|null} hintOrderedWords - same as passed to `GridGrid`
 * @param {number} [maxHintWords] - remaining hint slots (defaults to GRID_HINT_WORD_LIMIT)
 * @returns {Set<string>}
 */
export function getGridHintTargetWordsNormalized(filledCoordinates, wordsFoundList, hintOrderedWords, maxHintWords) {
    const coords = normalizeFilledCoords(filledCoordinates);
    const foundSet = new Set(
        (wordsFoundList || [])
            .map((w) => normalizeWord(typeof w === "string" ? w : String(w)))
            .filter(Boolean)
    );

    const cap =
        typeof maxHintWords === "number" && Number.isFinite(maxHintWords) && maxHintWords >= 0
            ? Math.min(GRID_HINT_WORD_LIMIT, maxHintWords)
            : GRID_HINT_WORD_LIMIT;

    if (cap <= 0) {
        return new Set();
    }

    let limited;
    if (Array.isArray(hintOrderedWords) && hintOrderedWords.length > 0) {
        const byWord = new Map();
        for (const coord of coords) {
            const w = (coord.text || coord.word || "").trim();
            if (!w) continue;
            const key = w.toLowerCase();
            if (!byWord.has(key)) byWord.set(key, coord);
        }
        limited = [];
        for (const w of hintOrderedWords) {
            const key = w.trim().toLowerCase();
            if (foundSet.has(key)) continue;
            const coord = byWord.get(key);
            if (coord == null) continue;
            if (gridCoordGetFirstPosition(coord) == null) continue;
            limited.push(coord);
            if (limited.length >= cap) break;
        }
    } else {
        const eligible = coords.filter((coord) => {
            const wordInCoord = (coord.text || coord.word || "").trim().toLowerCase();
            if (wordInCoord && foundSet.has(wordInCoord)) return false;
            return gridCoordGetFirstPosition(coord) != null;
        });
        limited = eligible.slice(0, cap);
    }

    const out = new Set();
    for (const coord of limited) {
        const nw = normalizeWord(coord.text || coord.word || "");
        if (nw) out.add(nw);
    }
    return out;
}

/**
 * Normalized words that currently receive a middle-letter hint (crossword), before the solved word is added.
 * Matches `CrossPlayMain` `hintRevealCells` word selection (not random cell index).
 *
 * @param {object[]} filledCoordinates
 * @param {string[]} wordsFoundList
 * @param {number} [maxHintWords] - remaining hint slots (defaults to CROSSWORD_HINT_WORD_LIMIT)
 * @returns {Set<string>}
 */
export function getCrosswordHintTargetWordsNormalized(filledCoordinates, wordsFoundList, maxHintWords) {
    const coords = normalizeFilledCoords(filledCoordinates);
    if (!coords.length) return new Set();

    const cap =
        typeof maxHintWords === "number" && Number.isFinite(maxHintWords) && maxHintWords >= 0
            ? Math.min(CROSSWORD_HINT_WORD_LIMIT, maxHintWords)
            : CROSSWORD_HINT_WORD_LIMIT;

    const withPositions = coords.filter(
        (c) => (c.text || c.word) && c.positions && c.positions.length > 0 && c.direction !== "scattered"
    );
    const foundArr = wordsFoundList || [];
    const unresolved = withPositions.filter((c) => {
        const word = normalizeWord(c.text || c.word || "");
        return word && !foundArr.includes(word);
    });
    const selected = unresolved.slice(0, cap);
    const out = new Set();
    for (const c of selected) {
        const w = normalizeWord(c.text || c.word || "");
        if (w) out.add(w);
    }
    return out;
}

/**
 * Merge `hintUsed` string onto play puzzle state (top-level and nested `template`).
 * @param {object|null|undefined} prev
 * @param {string|undefined} hu
 */
export function mergePuzzleHintUsedIntoState(prev, hu) {
    if (!prev || hu === undefined) return prev;
    const normalized = normalizeHintUsedToMultilineString(hu);
    const next = { ...prev, hintUsed: normalized };
    if (prev.template && typeof prev.template === "object") {
        next.template = { ...prev.template, hintUsed: normalized };
    }
    return next;
}

/**
 * `hintUsed` on play RO: newline-separated words (string). Puzzle load may expose it under `template` from GET `/home/cross/:id` / `/home/grid/:id`; startPlay / hint-used return it too.
 * @param {object|null|undefined} ro - startPlay / hint-used / progress response body
 * @returns {string|undefined}
 */
export function hintUsedStringFromPlayRo(ro) {
    if (!ro || typeof ro !== "object") return undefined;
    const v = ro.hintUsed ?? ro.hint_used;
    if (v == null) return undefined;
    return normalizeHintUsedToMultilineString(v);
}
